import React, { useEffect, useState } from 'react'
import { gql } from '@apollo/client'
import { useLazyQuery, useQuery, useMutation } from '../../Hooks/graphql_utils'
import { CAN_EDIT_GUNITE_SCHEDULE, CAN_EDIT_PLASTER_SCHEDULE } from '../../utils/enums'
import {
    Map, CardWithBreadcrumb, Row, Col, Card, Divider, CheckboxInput,
    Button, CalendarDatePicker, PopoverInteractionKind, Position, Popover,
    Spinner, H4, H3, ErrorMessage, FormInput, FormSimpleSelect
} from '../../Elements'
import moment from 'moment'
import { useHasRight } from '../../Hooks/hasRight'
import { colors } from '../../Elements/Map/colors'
import { AddressLinkText } from "../../Elements/Address";
import { useMediaQuery } from "react-responsive";
import { CanViewJob } from "../Auth";

const GUNITE_COMPANY_DIVISION = 2
const PEBBLE_COMPANY_DIVISION = 3
const DEFAULT_COLOR = 'lightgray'
const DEFAULT_UNASSIGNED_COLOR = 'black'
const DEFAULT_JOBS_BY_APP_COLOR = '#313131'
const DEFAULT_JOBS_BY_APP_WITH_CREW_COLOR = '#f1f1f1'

const FRAGMENT_PRESITE_SCHEDULE = gql`
    fragment PresiteScheduleFragment on PresiteSchedule {
        presite_supervisor_id
        job {
            id
            name
            address_line_1
            address_line_2
            city
            state
            zip_code
            lat
            lon
            company{
                id
                company_name
            }

        }
        job_detail{
            id
            job_category{
                id
            }
        }
        job_presite_details_id
        crew_id
    }
    `

const QUERY = gql`
    query presiteScheduleQuery($presite_date: String!, $start_date: String!, $end_date: String!, $company_divisions: [Int]!) {
        lookupCompanyDivisions {
            id
            name
        }
        presiteSupervisors{
            id
            company_division_id,
            sort_order
        }
        lookupEmployee{
            id
            name
            company_division{
                id
            }
        }
        lookupQuickFocusMap {
            id
            latitude
            longitude
        }
        presiteSchedule(presite_date: $presite_date){
            ...PresiteScheduleFragment
        }

        jobsByAppDate(start_date: $start_date, end_date: $end_date, company_divisions: $company_divisions){
            ...PresiteScheduleFragment
        }

    }
    ${FRAGMENT_PRESITE_SCHEDULE}
    `

const LAZY_QUERY_PRESITE_SCHEDULE = gql`
    query presiteScheduleLazyQuery ($presite_date: String!){
        presiteSchedule(presite_date: $presite_date){
            ...PresiteScheduleFragment
        }
    }
    ${FRAGMENT_PRESITE_SCHEDULE}
`
const LAZY_QUERY_JOBS_BY_APP_DATE = gql`
    query jobsByAppDateLazyQuery ($start_date: String!, $end_date: String!, $company_divisions: [Int]! ){
        jobsByAppDate(start_date: $start_date, end_date: $end_date, company_divisions: $company_divisions){
            ...PresiteScheduleFragment
        }
    }
    ${FRAGMENT_PRESITE_SCHEDULE}
`

const LAZY_QUERY_SCHEDULED_UNSCHEDULED_JOBS = gql`
    query presiteScheduleJobsByAppLazyQuery ($presite_date: String!, $start_date: String!, $end_date: String!, $company_divisions: [Int]!){
        presiteSchedule(presite_date: $presite_date){
            ...PresiteScheduleFragment
        }
        jobsByAppDate(start_date: $start_date, end_date: $end_date, company_divisions: $company_divisions){
            ...PresiteScheduleFragment
        }
    }
    ${FRAGMENT_PRESITE_SCHEDULE}
`

const PRESITE_ASSIGN_MUTATION = gql`
    mutation UpdatePresite ($presiteId: Int, $employeeId: Int, $scheduledDate: Date) {
        updatePresiteSchedule(presite_id: $presiteId, employee_id: $employeeId, scheduled_date: $scheduledDate)
    }
`

const LOOKUP_QUERY = gql`
    query mapLookup{
        lookupQuickFocusMap {
            id
            latitude
            longitude
        }
    }
`

const supervisorHeader = {
    minHeight: 50,
    border: 'solid black 2px',
    backgroundColor: DEFAULT_COLOR,
    textAlign: 'center',
    textTransform: 'uppercase',
    color: 'white',
    fontWeight: 'bold',
    fontSize: '12pt',
    padding: '10px 2px'
}

const addressStyle = {
    fontSize: '12pt',
    fontWeight: 'bold',
    marginLeft: 20,
    paddingTop: 5
}

const jobNameStyle = {
    color: '#106ba3',
    fontSize: 'large'
}

const LinkToJob = ({ jobId, jobDetailId }) => (
    <CanViewJob>
        <Button minimal icon='share' onClick={() => window.open(`/job/${jobId}/edit/${jobDetailId}`, "_blank")} />
    </CanViewJob>
)

const MapMarker = ({ job, companyDivisionId, jobPresiteId, jobDetailId, supervisorList, supervisor, selectedPresiteDate, fetchScheduledAndUnscheduled, closeCurrentPopup }) => {
    const [updatePresiteSchedule] = useMutation(PRESITE_ASSIGN_MUTATION)

    return (
        <>
            <div style={jobNameStyle}>{job.name}<LinkToJob jobId={job.id} jobDetailId={jobDetailId} /></div>
            <div>{job.company.company_name}</div>
            <div>
                <AddressLinkText
                    line1={job.address_line_1}
                    line2={job.address_line_2}
                    city={job.city}
                    state={job.state}
                    zip={job.zip_code}
                />
            </div>
            <div style={{ marginTop: 5 }}>
                <FormSimpleSelect
                    items={supervisorList.filter(s => s.company_division?.id === companyDivisionId && s.id > 0)}
                    label='Assigned To:'
                    value={supervisor}
                    nullable
                    onChange={async (val) => {
                        await updatePresiteSchedule({
                            variables: {
                                presiteId: jobPresiteId,
                                employeeId: (val && val.id) ? val.id : null,
                                scheduledDate: selectedPresiteDate.format('YYYY-MM-DD')
                            }
                        }).then(async () => {
                            await fetchScheduledAndUnscheduled()
                            if (closeCurrentPopup) {
                                closeCurrentPopup()

                            }
                        })
                    }}
                />
            </div>
        </>)
}

const AssignSupervisorColorScheme = (supervisors, colorScheme) => {

    return supervisors.map((s, idx) => {
        return (
            {
                ...s,
                color: (colorScheme.length > idx
                    && colorScheme[idx] !== DEFAULT_JOBS_BY_APP_COLOR
                    && colorScheme[idx] !== DEFAULT_JOBS_BY_APP_WITH_CREW_COLOR
                    && colorScheme[idx])
                    || DEFAULT_COLOR
            }
        )
    })
}

const BuildUnassignedSupervisors = (enabledCompanyDivisionSelections) => {
    const unassignedSupervisors = []

    if (enabledCompanyDivisionSelections.includes(GUNITE_COMPANY_DIVISION)) {
        unassignedSupervisors.push({
            id: -2,
            name: 'Unassigned Gunite Supervisor',
            company_division: {
                id: GUNITE_COMPANY_DIVISION
            },
            color: DEFAULT_UNASSIGNED_COLOR
        })
    }

    if (enabledCompanyDivisionSelections.includes(PEBBLE_COMPANY_DIVISION)) {
        unassignedSupervisors.push({
            id: -3,
            name: 'Unassigned Pebble Supervisor',
            company_division: {
                id: PEBBLE_COMPANY_DIVISION
            },
            color: DEFAULT_UNASSIGNED_COLOR
        })
    }

    return unassignedSupervisors
}

const BuildSupervisorSchedule = (supervisors, presiteSchedule) => {
    const mappedSchedule = []
    supervisors.forEach(s => {
        if (s.id < 0) {
            const jobCategory = s.company_division?.id === GUNITE_COMPANY_DIVISION ? 1 : 2
            mappedSchedule.push({
                supervisor: s,
                schedule: presiteSchedule.filter(p => p.presite_supervisor_id === null && p.job_detail?.job_category?.id === jobCategory)
            })
        }
        else {
            mappedSchedule.push({
                supervisor: s,
                schedule: presiteSchedule.filter(p => p.presite_supervisor_id === s.id)
            })
        }
    })

    return mappedSchedule
}

const BuildUnassignedSchedule = (presiteSchedule) => {
    return presiteSchedule.filter(p => p.presite_supervisor_id === null).map(d => {
        return {
            ...d,
            company_division: d.job_detail?.job_category?.id === 1 ? GUNITE_COMPANY_DIVISION : PEBBLE_COMPANY_DIVISION
        }
    })
}

const BuildMapList = (supervisorSchedule, jobsByAppDate, supervisorList, selectedPresiteDate, fetchScheduledAndUnscheduled,
    selectedCompanyDivisions) => {
    let mappedJobs = []

    const supervisorJobs = []
    supervisorSchedule.filter(ss => ss.schedule && ss.schedule.length > 0 && selectedCompanyDivisions.includes(ss.supervisor.company_division?.id)).forEach(s => {
        s.schedule.filter(j => j.job.lat !== null && j.job.lon !== null).forEach(sch => {
            supervisorJobs.push(
                {
                    position: [sch.job.lat, sch.job.lon],
                    markerColor: s.supervisor.color,
                    children: <MapMarker
                        key={`marker_${sch.job.id}`}
                        job={sch.job}
                        companyDivisionId={s.supervisor.company_division.id}
                        jobDetailId={sch.job_detail.id}
                        jobPresiteId={sch.job_presite_details_id}
                        supervisorList={supervisorList}
                        supervisor={s.supervisor}
                        selectedPresiteDate={selectedPresiteDate}
                        fetchScheduledAndUnscheduled={fetchScheduledAndUnscheduled}
                    />
                }
            )
        })
    })

    const mappedJobsByAppDate = jobsByAppDate.filter(j => j.job?.lat !== null && j.job?.lon !== null
        && selectedCompanyDivisions.includes(j.company_division)).map(m => ({
            position: [m.job.lat, m.job.lon],
            markerColor: m.crew_id ? DEFAULT_JOBS_BY_APP_WITH_CREW_COLOR : DEFAULT_JOBS_BY_APP_COLOR,
            children: <MapMarker
                key={`marker_${m.job.id}`}
                job={m.job}
                companyDivisionId={m.company_division}
                jobPresiteId={m.job_presite_details_id}
                jobDetailId={m.job_detail.id}
                supervisorList={supervisorList}
                selectedPresiteDate={selectedPresiteDate}
                fetchScheduledAndUnscheduled={fetchScheduledAndUnscheduled}
            />
        }))

    return mappedJobs.concat(supervisorJobs).concat(mappedJobsByAppDate)
}
const PresiteDatePicker = ({ setSelectedPresiteDate, selectedPresiteDate, fetchPresiteSchedule, presiteScheduleLoading }) => {
    const [isPopoverOpen, setIsPopoverOpen] = useState(false)

    const isMediumScreen = useMediaQuery({ query: '(max-width: 1200px)' })

    const handleInteraction = (nextState) => {
        setIsPopoverOpen(nextState)
    }

    const handleArrowClick = (isNextDayClick) => {
        const newCurrentDate = isNextDayClick
            ? moment(selectedPresiteDate.add(1, 'd'))
            : moment(selectedPresiteDate.subtract(1, 'd'))

        setSelectedPresiteDate(newCurrentDate)
    }
    const calendarPopover = (
        <div>
            <CalendarDatePicker
                maxDate={moment().add(5, 'years').toDate()}
                highlightCurrentDay={true}
                onChange={(chosenDate, isUserSelected) => {
                    if (chosenDate && isUserSelected) {
                        handleInteraction(false)

                        const newDate = moment(chosenDate)

                        setSelectedPresiteDate(newDate)

                    }
                }}
            />
        </div>
    );
    return (
        <div style={{ marginLeft: 1, textAlign: 'center', fontSize: '1.17em', fontWeight: 'bold' }}>
            <Button icon='chevron-left' minimal style={{ marginBottom: 3, marginRight: 10 }} onClick={() => handleArrowClick(false)} />
            {selectedPresiteDate.format(isMediumScreen ? 'M/D/YY (dddd)' : 'M/D/YY')}
            <Popover
                interactionKind={PopoverInteractionKind.CLICK}
                position={Position.BOTTOM}
                content={calendarPopover}
                isOpen={isPopoverOpen}
                onInteraction={(state) => handleInteraction(state)}
                children={
                    <>
                        {presiteScheduleLoading && <Spinner size={16} style={{ marginLeft: 5, marginBottom: 3 }} />}
                        {!presiteScheduleLoading && <Button icon='calendar' minimal style={{ marginLeft: 3, marginBottom: 3 }} />}
                    </>
                }
            />
            <Button icon='chevron-right' minimal style={{ marginBottom: 3 }} onClick={() => handleArrowClick(true)} />
        </div>
    )
}

const PresiteOptionsSection = ({ companyDivisionList, selectedCompanyDivisions, updateCompanyDivisionSelection, enabledCompanyDivisionSelections,
    selectedPresiteDate, setSelectedPresiteDate, fetchPresiteSchedule, presiteScheduleLoading }) => {

    return (
        <>
            <Row>
                <Col xs={12} xl={10}>
                    <H4>Presite Date</H4>
                </Col>
            </Row>
            <Row>
                <Col xs={12} sm={8} xl={6}>
                    <PresiteDatePicker
                        selectedPresiteDate={selectedPresiteDate}
                        setSelectedPresiteDate={setSelectedPresiteDate}
                        fetchPresiteSchedule={fetchPresiteSchedule}
                        presiteScheduleLoading={presiteScheduleLoading}

                    />
                </Col>
                <Col xs={12} sm={4} xl={4}>
                    {companyDivisionList.map(cd => {
                        return (
                            <CheckboxInput
                                label={cd.name}
                                disabled={!enabledCompanyDivisionSelections.includes(cd.id)}
                                id={'company_division_' + cd.name}
                                checked={selectedCompanyDivisions.includes(cd.id)}
                                value={cd.id}
                                onChange={(_) => updateCompanyDivisionSelection(_)}
                                groupStyles={{ display: 'inline-block', marginTop: '0px', paddingRight: '5px' }}
                                key={'criteria-company_division_' + cd.name}
                            />
                        )
                    })}
                </Col>
            </Row>


        </>
    )
}
const SupervisorSection = ({ companyDivisionList, selectedCompanyDivisions, updateCompanyDivisionSelection, enabledCompanyDivisionSelections,
    assignedPresiteSchedule, selectedPresiteDate, setSelectedPresiteDate, fetchPresiteSchedule, presiteScheduleLoading }) => {

    const formatAddress = (job) => {
        let formattedAddress = job.address_line_1 && job.address_line_2 ?
            ` - ${job.address_line_1} ${job.address_line_2}` : job.address_line_1 ?
                ` - ${job.address_line_1}` : ''

        formattedAddress = job.zip_code ? formattedAddress.concat(` - ${job.zip_code}`) : formattedAddress

        return formattedAddress
    }

    return (
        <>
            <PresiteOptionsSection
                companyDivisionList={companyDivisionList}
                selectedCompanyDivisions={selectedCompanyDivisions}
                updateCompanyDivisionSelection={updateCompanyDivisionSelection}
                enabledCompanyDivisionSelections={enabledCompanyDivisionSelections}
                selectedPresiteDate={selectedPresiteDate}
                setSelectedPresiteDate={setSelectedPresiteDate}
                fetchPresiteSchedule={fetchPresiteSchedule}
                presiteScheduleLoading={presiteScheduleLoading} />
            <Divider />
            {selectedCompanyDivisions.map((c, idx) => {
                const schedules = assignedPresiteSchedule.filter(s => s.supervisor?.company_division?.id === c)
                const companyDivisionName = companyDivisionList.find(cd => cd.id === c)?.name
                return (
                    <React.Fragment key={`${companyDivisionName}-${idx}`}>
                        <H3 style={{ padding: 5 }}>{`${companyDivisionName} Supervisors`}</H3>
                        {
                            schedules.map((s, idx) => {
                                return (
                                    <React.Fragment key={`sup-${s.supervisor.id}-${idx}`}>
                                        <div
                                            style={{
                                                margin: 5,
                                                paddingBottom: 2
                                            }}
                                        >
                                            <div style={{ ...supervisorHeader, backgroundColor: s.supervisor.color }}>{s.supervisor.name}</div>
                                            {
                                                s.schedule.map((sch, index) => {
                                                    return (
                                                        <React.Fragment key={`sch-${sch.job_presite_details_id}-${index}`}>
                                                            <div style={addressStyle}>{`${sch.job.id} - ${sch.job.name}${formatAddress(sch.job)}`}</div>
                                                        </React.Fragment>
                                                    )
                                                })
                                            }
                                        </div>
                                    </React.Fragment>
                                )
                            })
                        }
                    </React.Fragment>
                )
            })}
        </>
    )
}
const MapDateFilter = ({ selectedJobStartDate, selectedJobEndDate, setSelectedJobStartDate, setSelectedJobEndDate, fetchJobsByAppDate,
    jobsByAppDateLoading }) => {
    const isMobile = useMediaQuery({ query: '(max-width: 800px)' })

    const [dateSearchFilters, setDateSearchFilters] = useState({
        start_date: selectedJobStartDate,
        end_date: selectedJobEndDate
    })

    const UpdateDateFilter = (selectedDate, isStartDate) => {
        const newDate = moment(selectedDate)

        if (isStartDate) {
            setDateSearchFilters({
                ...dateSearchFilters,
                start_date: newDate
            })
        }
        else {
            setDateSearchFilters({
                ...dateSearchFilters,
                end_date: newDate
            })
        }
    }

    const RefreshJobs = () => {

        setSelectedJobStartDate(dateSearchFilters.start_date)
        setSelectedJobEndDate(dateSearchFilters.end_date)
    }

    return (
        <>
            <Row>
                <Col lg={3} md={4}>
                    <H4>Jobs to be Applied Between </H4>
                </Col>
                <Col lg={9} md={8}>
                    <Row>
                        <Col xs={isMobile ? 5 : 4}>
                            <FormInput
                                id='jobs-from_date'
                                type='Date'
                                disabled={jobsByAppDateLoading}
                                value={dateSearchFilters.start_date.format('YYYY-MM-DD')}
                                onChange={(evt) => UpdateDateFilter(evt.target.value, true)}
                            />
                        </Col>
                        <Col xs={isMobile ? 2 : 1} style={{ textAlign: 'center' }}>
                            <H4> and </H4>
                        </Col>
                        <Col xs={isMobile ? 5 : 4}>
                            <FormInput
                                id='jobs-to_date'
                                type='Date'
                                disabled={jobsByAppDateLoading}
                                value={dateSearchFilters.end_date.format('YYYY-MM-DD')}
                                onChange={(evt) => {
                                    UpdateDateFilter(evt.target.value, false)
                                }}
                            />
                        </Col>
                        {!isMobile &&
                            <Col xs={3}>
                                <Button
                                    style={{ float: 'right' }}
                                    disabled={jobsByAppDateLoading}
                                    text={jobsByAppDateLoading ? 'Fetching data...' : 'Search'}
                                    onClick={() => RefreshJobs()}
                                    large
                                    className='searchButton' />
                            </Col>
                        }
                    </Row>
                </Col>
            </Row>
            {isMobile &&
                <Row>
                    <Col lg={3} md={4}>
                        <Button
                            style={{ width: '100%' }}
                            disabled={jobsByAppDateLoading}
                            text={jobsByAppDateLoading ? 'Fetching data...' : 'Search'}
                            onClick={() => RefreshJobs()}
                            large
                            className='searchButton' />
                    </Col>
                </Row>
            }
        </>
    )
}
const MapSection = ({ selectedJobStartDate, selectedJobEndDate, setSelectedJobStartDate, setSelectedJobEndDate, mappedJobs, fetchJobsByAppDate,
    jobsByAppDateLoading, mapCenter }) => {
    return (
        <>
            <MapDateFilter
                selectedJobStartDate={selectedJobStartDate}
                selectedJobEndDate={selectedJobEndDate}
                setSelectedJobStartDate={setSelectedJobStartDate}
                setSelectedJobEndDate={setSelectedJobEndDate}
                fetchJobsByAppDate={fetchJobsByAppDate}
                jobsByAppDateLoading={jobsByAppDateLoading}
            />
            <Divider />
            <Map
                center={mapCenter}
                height="600px"
                width="100%"
                markers={mappedJobs}
            />
            <Divider />
            <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-around' }}>
                <p style={{ fontSize: '12pt', fontWeight: 'bold' }}><img
                    src={require('../../Elements/Map/icons/marker-icon-2x-313131.png')}
                    alt='Unscheduled Presite Job/Unassigned Crew'
                    width='20px'
                /> - Unscheduled Presite Job/Unassigned Crew</p>
                <p style={{ fontSize: '12pt', fontWeight: 'bold' }}><img
                    src={require(`../../Elements/Map/icons/marker-icon-2x-f1f1f1.png`)}
                    alt='Unscheduled Presite Job/Assigned Crew'
                    width='20px'
                /> - Unscheduled Presite Job/Assigned Crew</p>
            </div>

        </>
    )
}

const PresiteScheduleWrapper = ({ companyDivisionList, enabledCompanyDivisionSelections, presiteDate, presiteSupervisorSchedule, presiteSupervisors,
    jobsFromDate, jobsToDate, jobsByAppDate }) => {
    const [selectedCompanyDivisions, setSelectedCompanyDivisions] = useState(enabledCompanyDivisionSelections)
    const [assignedPresiteSchedule, setAssignedPresiteSchedule] = useState(presiteSupervisorSchedule)
    const [scheduledJobs, setScheduledJobs] = useState(jobsByAppDate)
    const [selectedPresiteDate, setSelectedPresiteDate] = useState(presiteDate)
    const [selectedJobStartDate, setSelectedJobStartDate] = useState(jobsFromDate)
    const [selectedJobEndDate, setSelectedJobEndDate] = useState(jobsToDate)
    const [supervisorList] = useState(presiteSupervisors)
    const [mappedJobs, setMappedJobs] = useState([])

    const { error: lookupDataError, data: lookupData, loading: lookupDataLoading } = useQuery(LOOKUP_QUERY, {
    })

    const mapCity = lookupData.lookupQuickFocusMap.find(qf => qf.id === 1)
    const mapCenter = [mapCity.latitude, mapCity.longitude]

    const [fetchPresiteSchedule, { loading: presiteScheduleLoading, error: presiteScheduleError }] = useLazyQuery(LAZY_QUERY_PRESITE_SCHEDULE, {
        variables: {
            presite_date: selectedPresiteDate.format('YYYY-MM-DD')
        },
        onCompleted: data => {
            const supervisorSchedule = BuildSupervisorSchedule(supervisorList, data.presiteSchedule)

            const mapList = BuildMapList(supervisorSchedule, scheduledJobs, supervisorList,
                selectedPresiteDate, fetchScheduledAndUnscheduled, selectedCompanyDivisions)

            setAssignedPresiteSchedule(supervisorSchedule)
            setMappedJobs(mapList)
        }
    })

    const [fetchJobsByAppDate, { loading: jobsByAppDateLoading, error: jobsByAppDateError }] = useLazyQuery(LAZY_QUERY_JOBS_BY_APP_DATE, {
        variables: {
            start_date: selectedJobStartDate.format('YYYY-MM-DD'),
            end_date: selectedJobEndDate.format('YYYY-MM-DD'),
            company_divisions: enabledCompanyDivisionSelections
        },
        onCompleted: (data) => {
            const jobs = BuildUnassignedSchedule(data.jobsByAppDate)

            const mapList = BuildMapList(assignedPresiteSchedule, jobs,
                supervisorList, selectedPresiteDate, fetchScheduledAndUnscheduled, selectedCompanyDivisions)

            setScheduledJobs(jobs)
            setMappedJobs(mapList)
        }
    })

    const [fetchScheduledAndUnscheduled, { error: scheduledUnscheduledError }] = useLazyQuery(LAZY_QUERY_SCHEDULED_UNSCHEDULED_JOBS, {
        variables: {
            presite_date: selectedPresiteDate.format('YYYY-MM-DD'),
            start_date: selectedJobStartDate.format('YYYY-MM-DD'),
            end_date: selectedJobEndDate.format('YYYY-MM-DD'),
            company_divisions: enabledCompanyDivisionSelections
        },
        onCompleted: (data) => {
            const supervisorSchedule = BuildSupervisorSchedule(supervisorList, data.presiteSchedule)
            const jobs = BuildUnassignedSchedule(data.jobsByAppDate)

            const mapList = BuildMapList(supervisorSchedule, jobs, supervisorList,
                selectedPresiteDate, fetchScheduledAndUnscheduled, selectedCompanyDivisions)

            setAssignedPresiteSchedule(supervisorSchedule)
            setScheduledJobs(jobs)
            setMappedJobs(mapList)

        }
    })

    const updateCompanyDivisionSelection = (newValue) => {
        let newSelectedValues = []
        const companyDivisionValue = parseInt(newValue.currentTarget.value)

        if (!newValue.currentTarget.checked) {
            const idx = selectedCompanyDivisions.indexOf(companyDivisionValue)
            if (idx > -1) {
                newSelectedValues = selectedCompanyDivisions.filter((v, index) => index !== idx)
            }
        }
        else {
            newSelectedValues = newSelectedValues.concat(selectedCompanyDivisions).concat(companyDivisionValue)
        }

        newSelectedValues = newSelectedValues.sort((a, b) => (a - b))

        const filteredMapJobs = BuildMapList(
            assignedPresiteSchedule.filter(s => newSelectedValues.includes(s.supervisor?.company_division?.id)),
            scheduledJobs.filter(s => newSelectedValues.includes(s.company_division)),
            supervisorList,
            presiteDate,
            fetchScheduledAndUnscheduled,
            newSelectedValues
        )

        setSelectedCompanyDivisions(newSelectedValues)
        setMappedJobs(filteredMapJobs)
    }

    useEffect(() => {
        const mapList = BuildMapList(presiteSupervisorSchedule, jobsByAppDate, presiteSupervisors,
            presiteDate, fetchScheduledAndUnscheduled, enabledCompanyDivisionSelections)
        setMappedJobs(mapList)
        setAssignedPresiteSchedule(presiteSupervisorSchedule)
        fetchPresiteSchedule()
    }, [presiteSupervisorSchedule, jobsByAppDate, presiteSupervisors, presiteDate,
        fetchPresiteSchedule, fetchScheduledAndUnscheduled, enabledCompanyDivisionSelections])


    useEffect(() => {
        fetchPresiteSchedule()
    }, [selectedPresiteDate, fetchPresiteSchedule])

    useEffect(() => {
        fetchJobsByAppDate()
    }, [setSelectedJobStartDate, setSelectedJobEndDate, fetchJobsByAppDate])

    if (lookupDataLoading) {
        return <Spinner />
    }

    if (presiteScheduleError || jobsByAppDateError || scheduledUnscheduledError || lookupDataError) {
        return <ErrorMessage />
    }

    return (
        <>
            <CardWithBreadcrumb items={[{ text: 'Presite Schedule', icon: 'path-search' }]} />
            <Row>
                <Col lg={12}>
                    <Card>
                        <Row>
                            <Col lg={4} xl={2}>
                                <SupervisorSection
                                    companyDivisionList={companyDivisionList}
                                    selectedCompanyDivisions={selectedCompanyDivisions}
                                    updateCompanyDivisionSelection={updateCompanyDivisionSelection}
                                    enabledCompanyDivisionSelections={enabledCompanyDivisionSelections}
                                    assignedPresiteSchedule={assignedPresiteSchedule}
                                    selectedPresiteDate={selectedPresiteDate}
                                    setSelectedPresiteDate={setSelectedPresiteDate}
                                    fetchPresiteSchedule={fetchPresiteSchedule}
                                    presiteScheduleLoading={presiteScheduleLoading}
                                />
                            </Col>

                            <Col lg={8} xl={10}>

                                <MapSection
                                    selectedJobStartDate={selectedJobStartDate}
                                    selectedJobEndDate={selectedJobEndDate}
                                    setSelectedJobStartDate={setSelectedJobStartDate}
                                    setSelectedJobEndDate={setSelectedJobEndDate}
                                    mappedJobs={mappedJobs}
                                    fetchJobsByAppDate={fetchJobsByAppDate}
                                    jobsByAppDateLoading={jobsByAppDateLoading}
                                    mapCenter={mapCenter}
                                />
                            </Col>
                        </Row>
                    </Card>
                </Col>
            </Row>
        </>
    )
}
export default () => {
    const isPebbleUser = useHasRight(CAN_EDIT_PLASTER_SCHEDULE)
    const isGuniteUser = useHasRight(CAN_EDIT_GUNITE_SCHEDULE)
    const presiteDate = moment()
    const jobsFromDate = moment().startOf('isoWeek')
    const jobsToDate = moment(jobsFromDate).add(1, 'w')

    //Get Selectable Company Divisions based on role
    let enabledCompanyDivisionSelections = []

    if (isGuniteUser)
        enabledCompanyDivisionSelections.push(GUNITE_COMPANY_DIVISION)
    if (isPebbleUser)
        enabledCompanyDivisionSelections.push(PEBBLE_COMPANY_DIVISION)

    const { loading, error, data } = useQuery(QUERY, {
        fetchPolicy: 'network-only',
        variables: {
            presite_date: presiteDate.format('YYYY-MM-DD'),
            start_date: jobsFromDate.format('YYYY-MM-DD'),
            end_date: jobsToDate.format('YYYY-MM-DD'),
            company_divisions: enabledCompanyDivisionSelections
        }
    })

    if (loading) {
        return <Spinner />
    }

    if (error) {
        return <ErrorMessage />
    }

    //Filter to Gunite and Pebble Company Divisions and Presite Supervisor Employees only
    const filteredCompanyDivisions = data.lookupCompanyDivisions.filter(c => c.id === GUNITE_COMPANY_DIVISION || c.id === PEBBLE_COMPANY_DIVISION)
    let presiteSupervisors = data.lookupEmployee.filter(e =>
        data.presiteSupervisors.filter(s => enabledCompanyDivisionSelections.includes(s.company_division_id)).map(p => p.id).includes(e.id))

    //Create Color Scheme for Supervisors
    presiteSupervisors = AssignSupervisorColorScheme(presiteSupervisors, colors)

    //Build Unassigned Supervisors
    presiteSupervisors = presiteSupervisors.concat(BuildUnassignedSupervisors(enabledCompanyDivisionSelections))

    //Build Presite Schedule
    const presiteSupervisorSchedule = BuildSupervisorSchedule(presiteSupervisors, data.presiteSchedule)

    const jobsByAppDate = BuildUnassignedSchedule(data.jobsByAppDate)

    return (
        <PresiteScheduleWrapper
            companyDivisionList={filteredCompanyDivisions}
            enabledCompanyDivisionSelections={enabledCompanyDivisionSelections}
            presiteSupervisors={presiteSupervisors}
            presiteDate={presiteDate}
            presiteSupervisorSchedule={presiteSupervisorSchedule}
            jobsFromDate={jobsFromDate}
            jobsToDate={jobsToDate}
            jobsByAppDate={jobsByAppDate}
        />
    )

}
